import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, getFormValues, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { __ } from '../../core/utils';
import Select from '../../components/Select';
import DateTimePicker from '../../components/DateTimePicker';
import SearchableDropdown from '../../components/SearchableDropdown';
import ArrowDownSmallIcon from '../../assets/images/arrow-down-small-15x15.svg';
import Tooltip from '../../components/Tooltip';
import Button from '../../components/Button';
import classNames from 'classnames';
import { listPossibleSubstagesWithChildrenByStage } from '../../redux/actions/table/all_tasks';
import {
  listPossibleSpecificationProjects,
  listPossibleSpecificationStages,
} from '../../redux/actions/table/working_set_specifications';
import styles from '../../styles/forms/timesheets/AddTimesheetEntryWorkItemForm.scss';

const AddTimesheetEntryWorkItemForm = (props) => {
  const { values = {}, handleSubmit, change, onClose, onSubmit } = props;
  const prevProjectId = useRef();
  const prevStageId = useRef();
  const [possibleProjects, setPossibleProjects] = useState([]);
  const [possibleStages, setPossibleStages] = useState([]);
  const [possibleSubstagesWithChildren, setPossibleSubstagesWithChildren] =
    useState([]);
  const [selected, setSelected] = useState({
    values: [],
    type: null,
    substage_id: null,
    task_id: null,
  });

  useEffect(() => {
    listPossibleSpecificationProjects().then(({ response }) => {
      setPossibleProjects(response.data || []);
    });
  }, []);

  useEffect(() => {
    if (values.project_id) {
      listPossibleSpecificationStages(values.project_id).then(
        ({ response }) => {
          setPossibleStages(response.data || []);
        },
      );

      if (
        !_.isNil(prevProjectId.current) &&
        prevProjectId.current !== values.project_id
      ) {
        change('stage_id', null);
        change('task_id', null);
        change('substage_id', null);
        setSelected({
          values: [],
          type: null,
          substage_id: null,
          task_id: null,
        });
      }

      prevProjectId.current = values.project_id;
      return;
    }

    setPossibleStages([]);
    change('stage_id', null);
    prevProjectId.current = values.project_id;
  }, [change, values.project_id]);

  useEffect(() => {
    if (values.stage_id) {
      listPossibleSubstagesWithChildrenByStage(values.stage_id).then(
        ({ response }) => {
          setPossibleSubstagesWithChildren(response.data || []);
        },
      );

      if (
        !_.isNil(prevStageId.current) &&
        prevStageId.current !== values.stage_id
      ) {
        setSelected({
          values: [],
          type: null,
          substage_id: null,
          task_id: null,
        });
        change('task_id', null);
        change('substage_id', null);
      }

      prevStageId.current = values.stage_id;
      return;
    }

    setPossibleSubstagesWithChildren([]);
    setSelected({
      values: [],
      type: null,
      substage_id: null,
      task_id: null,
    });
    change('task_id', null);
    change('substage_id', null);
    prevStageId.current = values.stage_id;
  }, [change, values.stage_id]);

  const dropdownOptions = useMemo(
    () =>
      _.map(possibleSubstagesWithChildren, (option, index) => ({
        ...option,
        group_option_key:
          option?.group_option_key || `__group__${option?.group_id}`,
        option_key:
          option?.option_key ||
          ['be', option?.group_id, option?.value, option?.label, index].join(
            '_',
          ),
      })),
    [possibleSubstagesWithChildren],
  );

  const selectedTaskOption = _.find(dropdownOptions, {
    value: selected.task_id,
  });
  const selectedGroupOption = _.find(dropdownOptions, {
    group_id: selected.substage_id,
  });
  const selectedLabel =
    selected.type === 'group'
      ? _.get(selectedGroupOption, 'group')
      : _.get(selectedTaskOption, 'label');

  const _handleSubmit = (formData) => {
    const projectOption = _.find(possibleProjects, ['id', formData.project_id]);
    const stageOption = _.find(possibleStages, ['id', formData.stage_id]);
    const labelForSubstageTask =
      selected.type === 'group'
        ? _.get(selectedGroupOption, 'group')
        : _.get(selectedTaskOption, 'label');

    onSubmit({
      ...formData,
      project_label: projectOption
        ? [projectOption.code, projectOption.name].join(': ')
        : '-',
      stage_label: stageOption
        ? [_.padStart(stageOption.order, 2, '0'), stageOption.name].join(': ')
        : '-',
      substage_task_label: labelForSubstageTask || '-',
    });
  };

  return (
    <Form className={styles.wrapper} onSubmit={handleSubmit(_handleSubmit)}>
      <div className={styles.form}>
        <Field
          name='date'
          component={DateTimePicker}
          label={__('project-settings.general-form.date-range')}
          footer
          range
          disabled
        />

        <div className={styles.flex}>
          <div className={styles.f1}>
            <Field
              name='project_id'
              searchable
              component={Select}
              label={__('add-timesheet-form.project')}
              options={_.map(possibleProjects, (project) => ({
                value: project.id,
                label: [project.code, project.name].join(': '),
              }))}
              placeholder={__('add-timesheet-form.add-project')}
            />
          </div>
          <div className={styles.f1}>
            <Field
              name='stage_id'
              disabled={!values.project_id}
              searchable
              component={Select}
              label={__('add-timesheet-form.stage')}
              options={_.map(possibleStages, (stage) => ({
                value: stage.id,
                label: [_.padStart(stage.order, 2, '0'), stage.name].join(': '),
              }))}
              placeholder={__('add-timesheet-form.add-stage')}
            />
          </div>
        </div>

        <SearchableDropdown
          options={dropdownOptions}
          dropdownWrapperClassName={styles.dropdownWrapper}
          multiselect
          top={30}
          header={
            <div className={styles.dropdownHeader}>
              <Tooltip
                text={__('add-timesheet-form.substages-and-tasks')}
                className={styles.filterHeaderName}
              >
                {__('add-timesheet-form.substages-and-tasks')}
              </Tooltip>
              <span
                className={styles.filterHeaderClearAll}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected({
                    values: [],
                    type: null,
                    substage_id: null,
                    task_id: null,
                  });
                  change('task_id', null);
                  change('substage_id', null);
                }}
              >
                {__('add-timesheet-form.substages-and-tasks.clear')}
              </span>
            </div>
          }
          selectedValues={selected.values}
          name='timesheet_entries_work_item_selector'
          leftStyled
          onSelect={(item, groupLabel) => {
            if (item.groupHeading) {
              const groupValues = _.map(
                _.filter(dropdownOptions, (option) =>
                  item.group_id
                    ? option.group_id === item.group_id
                    : option.group === groupLabel,
                ),
                'option_key',
              );

              setSelected({
                values: [item.group_option_key, ...groupValues],
                type: 'group',
                substage_id: item.group_id || null,
                task_id: null,
              });
              change('task_id', null);
              change('substage_id', item.group_id || null);
              return;
            }

            setSelected({
              values: [item.option_key || item.value],
              type: 'child',
              substage_id: item.group_id || null,
              task_id: item.value,
            });
            change('task_id', item.value || null);
            change('substage_id', item.group_id || null);
          }}
          onRemove={() => {
            setSelected({
              values: [],
              type: null,
              substage_id: null,
              task_id: null,
            });
            change('task_id', null);
            change('substage_id', null);
          }}
        >
          <div className={styles.bubble}>
            <label className={styles.bubbleLabel}>
              {__('add-timesheet-form.substages-and-tasks')}
            </label>
            <span
              className={classNames(!selectedLabel && styles.bubblePlaceholder)}
            >
              {selectedLabel ||
                __('add-timesheet-form.choose-substage-or-task')}
            </span>
            <ArrowDownSmallIcon className={styles.bubbleArrow} />
          </div>
        </SearchableDropdown>

        <Field name='task_id' component='input' type='hidden' />
        <Field name='substage_id' component='input' type='hidden' />

        <div className={styles.footer}>
          <Button
            lightGray
            medium
            middleText={__('button.cancel')}
            onClick={onClose}
          />
          <Button
            lightBlue
            medium
            middleText={__('button.create')}
            submit
            disabled={!values.project_id || !values.stage_id}
          />
        </div>
      </div>
    </Form>
  );
};

export default reduxForm({
  form: 'timesheet_entries.add_work_item',
  enableReinitialize: true,
})(
  connect((state) => ({
    values: getFormValues('timesheet_entries.add_work_item')(state),
  }))(AddTimesheetEntryWorkItemForm),
);
